// src/utils/caseAnalyzerApiClient.js

const DEFAULT_TIMEOUT_MS = 45000;
const DEFAULT_UPLOAD_TIMEOUT_MS = 20 * 60 * 1000;
const DEFAULT_START_TIMEOUT_MS = 45000;
const DEFAULT_UPLOAD_URL_TIMEOUT_MS = 90000;
const DEFAULT_UPLOAD_COMPLETE_TIMEOUT_MS = 90000;

function buildUrl(path) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
  if (!base) return path;
  return `${base}${path}`;
}

function makeClientNonce() {
  return (
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}_${Math.random().toString(16).slice(2)}`
  );
}

function getLocale() {
  if (typeof navigator !== "undefined" && navigator.language) return navigator.language;
  return "en-US";
}

function isDebugEnabled(opts = {}) {
  return Boolean(
    opts.debug ||
      opts.debugCaseAnalyzer ||
      (typeof window !== "undefined" && window.__CASE_ANALYZER_DEBUG__)
  );
}

function debugLog(opts, label, payload = {}) {
  if (!isDebugEnabled(opts)) return;

  // Never log the actual Firebase token.
  const safePayload = { ...payload };
  if (Object.prototype.hasOwnProperty.call(safePayload, "idToken")) {
    safePayload.hasIdToken = Boolean(safePayload.idToken);
    delete safePayload.idToken;
  }

  // eslint-disable-next-line no-console
  console.log(`[CaseAnalyzerAPI] ${label}`, safePayload);
}

function requireJobId(jobId) {
  const value = String(jobId || "").trim();
  if (!value) throw new Error("Missing jobId.");
  return value;
}

function requireIdToken(opts = {}, label = "Case Analyzer API") {
  const token = String(opts.idToken || "").trim();
  if (!token) {
    throw new Error(`${label} requires a valid Firebase ID token. Please log in again.`);
  }
  return token;
}

function authHeaders(idToken, extra = {}) {
  const token = String(idToken || "").trim();
  const headers = { ...extra };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function withTimeout(timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const ms = Math.max(1000, Number(timeoutMs || DEFAULT_TIMEOUT_MS));
  const timer = setTimeout(() => controller.abort(), ms);
  return { controller, timer };
}


function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function durationMs(startedAt) {
  return Math.max(0, Math.round(nowMs() - Number(startedAt || 0)));
}

function sanitizeSignedUploadHeaders(headers = {}) {
  const safeHeaders = {};
  Object.entries(headers || {}).forEach(([key, value]) => {
    const normalizedKey = String(key || "").trim();
    const lowerKey = normalizedKey.toLowerCase();
    if (!normalizedKey || value === undefined || value === null || value === "") return;

    // Signed uploads must never receive the user's Firebase bearer token.
    if (lowerKey === "authorization" || lowerKey === "cookie" || lowerKey === "content-length") return;
    safeHeaders[normalizedKey] = String(value);
  });
  return safeHeaders;
}

function safeInvoke(callback, payload) {
  if (typeof callback !== "function") return;
  try {
    callback(payload);
  } catch (err) {
    // A UI callback must never interrupt or fail the actual upload.
    // eslint-disable-next-line no-console
    console.warn("Case Analyzer upload callback failed:", err);
  }
}

function emitUploadEvent(opts = {}, event = {}) {
  const callbacks = new Set([
    opts.onUploadEvent,
    opts.onProgress,
    opts.onFileProgress,
  ]);

  if (event.type === "batch_start") callbacks.add(opts.onBatchStart);
  if (event.type === "batch_complete") callbacks.add(opts.onBatchComplete);
  if (event.type === "file_start") callbacks.add(opts.onFileStart);
  if (event.type === "upload_progress") callbacks.add(opts.onUploadProgress);
  if (event.type === "file_complete") callbacks.add(opts.onFileComplete);
  if (event.type === "file_error") callbacks.add(opts.onFileError);

  callbacks.forEach((callback) => safeInvoke(callback, event));
}

function buildUploadUserMessage(stage, fileName = "this file") {
  const name = String(fileName || "this file");

  if (stage === "file_upload_timeout") {
    return `${name} took too long to upload. This upload session has stopped. Please try again or use a smaller file.`;
  }

  if (stage === "file_upload") {
    return `We couldn’t upload ${name}. Please check your internet connection and try again.`;
  }

  if (stage === "upload_confirmation") {
    return `${name} was uploaded, but we couldn’t confirm it. This upload session has stopped. Please try again.`;
  }

  if (stage === "missing_file") {
    return `We couldn’t access ${name}. Please select the file again and try again.`;
  }

  return `We couldn’t prepare ${name} for upload. This upload session has stopped. Please try again.`;
}

function createUploadStageError(stage, cause, context = {}) {
  const fileName = context.fileName || context.originalName || "this file";
  const userMessage = context.userMessage || buildUploadUserMessage(stage, fileName);
  const technicalMessage = String(cause?.technicalMessage || cause?.message || cause || userMessage);
  const err = new Error(userMessage);

  err.name = "CaseAnalyzerUploadError";
  err.code = "CASE_ANALYZER_UPLOAD_FAILED";
  err.uploadStage = stage;
  err.failureStage = stage;
  err.phase = stage;
  err.userMessage = userMessage;
  err.technicalMessage = technicalMessage;
  err.jobId = context.jobId || "";
  err.fileName = fileName;
  err.fileIndex = Number(context.fileIndex || 0);
  err.fileNumber = Number(context.fileNumber || context.fileIndex || 0);
  err.totalFiles = Number(context.totalFiles || 0);
  err.sizeBytes = Number(context.sizeBytes || 0);
  err.elapsedMs = Number(context.elapsedMs || 0);
  err.timings = context.timings || {};
  err.status = Number(cause?.status || cause?.statusCode || 0) || undefined;
  err.statusCode = err.status;
  err.response = cause?.response;
  err.rawText = cause?.rawText;
  err.originalError = cause || null;
  return err;
}

export function getCaseAnalyzerUploadUserMessage(err) {
  if (!err) return "The upload could not be completed. Please try again.";
  if (err.userMessage) return String(err.userMessage);
  if (err.uploadStage || err.failureStage) {
    return buildUploadUserMessage(err.uploadStage || err.failureStage, err.fileName || "this file");
  }
  return "The upload could not be completed. Your selected files are still available. Please try again.";
}

function toInt(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : fallback;
}

function normalizePageCountStatus(value, pageCount) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw) return raw;
  return pageCount > 0 ? "provided" : "unknown";
}

function buildEvidencePackageMeta(evidenceItems = [], opts = {}) {
  const payload = opts.payload || opts.caseAnalyzerPayload || {};
  const payloadEvidence = payload?.evidence_package || payload?.evidencePackage || {};
  const payloadBilling = payload?.billing_package || payload?.billingPackage || {};

  const detectedPageCount = toInt(
    payloadEvidence.detected_page_count ?? payloadEvidence.detectedPageCount,
    0
  ) || (Array.isArray(evidenceItems)
    ? evidenceItems.reduce((sum, item) => sum + toInt(item?.detected_page_count ?? item?.detectedPageCount, 0), 0)
    : 0);

  const userEnteredPageCount = toInt(
    payloadEvidence.user_entered_page_count ?? payloadEvidence.userEnteredPageCount,
    0
  ) || (Array.isArray(evidenceItems)
    ? evidenceItems.reduce((sum, item) => sum + toInt(item?.manual_page_count ?? item?.manualPageCount, 0), 0)
    : 0);

  const declaredTotalPageCount = toInt(
    payloadEvidence.declared_total_page_count ??
      payloadEvidence.declaredTotalPageCount ??
      payloadEvidence.total_page_count ??
      payloadEvidence.totalPageCount ??
      payloadBilling.declared_total_page_count ??
      payloadBilling.declaredTotalPageCount,
    detectedPageCount + userEnteredPageCount
  );

  const requestedPageCount = toInt(
    payloadBilling.requested_page_count ??
      payloadBilling.requestedPageCount ??
      declaredTotalPageCount,
    declaredTotalPageCount
  );

  const matchedPageTier = toInt(
    payloadBilling.matched_page_tier ??
      payloadBilling.matchedPageTier ??
      payloadEvidence.matched_case_analyzer_page_tier ??
      payloadEvidence.matchedCaseAnalyzerPageTier,
    0
  );

  const maxAvailablePageTier = toInt(
    payloadBilling.max_available_page_tier ??
      payloadBilling.maxAvailablePageTier ??
      payloadEvidence.max_available_case_analyzer_page_tier ??
      payloadEvidence.maxAvailableCaseAnalyzerPageTier,
    0
  );

  const unknownPageCountFiles = toInt(
    payloadEvidence.unknown_page_count_files ?? payloadEvidence.unknownPageCountFiles,
    0
  );

  const missingManualPageCountFiles = toInt(
    payloadEvidence.missing_manual_page_count_files ??
      payloadEvidence.missingManualPageCountFiles ??
      payloadBilling.missing_manual_page_count_files ??
      payloadBilling.missingManualPageCountFiles,
    0
  );

  return {
    product: "case_analyzer",
    detected_page_count: detectedPageCount,
    detectedPageCount,
    user_entered_page_count: userEnteredPageCount,
    userEnteredPageCount,
    declared_total_page_count: declaredTotalPageCount,
    declaredTotalPageCount,
    requested_page_count: requestedPageCount,
    requestedPageCount,
    matched_page_tier: matchedPageTier || null,
    matchedPageTier: matchedPageTier || null,
    max_available_page_tier: maxAvailablePageTier,
    maxAvailablePageTier: maxAvailablePageTier,
    unknown_page_count_files: unknownPageCountFiles,
    unknownPageCountFiles,
    missing_manual_page_count_files: missingManualPageCountFiles,
    missingManualPageCountFiles,
  };
}

function normalizeApiErrorMessage(data, res, fallbackLabel) {
  const status = res?.status;
  const backendMessage =
    data?.error ||
    data?.message ||
    data?.reason ||
    data?.detail ||
    data?.billing?.error ||
    "";

  if (status === 401) {
    return backendMessage || "Your login session expired. Please log in again.";
  }

  if (status === 402) {
    return backendMessage || "No Case Analyzer reports are available on this account.";
  }

  if (status === 403) {
    return backendMessage || "You do not have access to this Case Analyzer job.";
  }

  if (status === 404) {
    return backendMessage || "The requested Case Analyzer job was not found.";
  }

  if (status === 409) {
    return backendMessage || "This Case Analyzer job is already locked or processing.";
  }

  if (status === 413) {
    return backendMessage || "This evidence package is too large for Case Analyzer.";
  }

  return backendMessage || `HTTP_${status || "UNKNOWN"}` || `${fallbackLabel} request failed.`;
}

function isAlreadyLockedOrStartedMessage(value) {
  const text = String(value || "").toLowerCase();
  return (
    text.includes("already locked") ||
    text.includes("already queued") ||
    text.includes("already started") ||
    text.includes("already processing") ||
    text.includes("locked for analysis") ||
    text.includes("queued for analysis") ||
    text.includes("processing")
  );
}

export function isCaseAnalyzerAlreadyStartedError(err) {
  if (!err) return false;
  const status = Number(err.status || err.statusCode || 0);
  if (status !== 400 && status !== 409) return false;

  return (
    isAlreadyLockedOrStartedMessage(err.message) ||
    isAlreadyLockedOrStartedMessage(err.response?.error) ||
    isAlreadyLockedOrStartedMessage(err.response?.message) ||
    isAlreadyLockedOrStartedMessage(err.response?.reason) ||
    isAlreadyLockedOrStartedMessage(err.response?.detail)
  );
}

function normalizeJobStatusPayload(data, fallbackJobId = "") {
  const job = data?.job || data || {};
  const progress = data?.progress || job?.progress || {};
  const status = data?.status || job?.status || progress?.stage || "";

  return {
    ...data,
    job,
    progress,
    status,
    job_id: data?.job_id || data?.jobId || job?.job_id || job?.jobId || fallbackJobId,
    jobId: data?.jobId || data?.job_id || job?.jobId || job?.job_id || fallbackJobId,
  };
}

function normalizeHistoryPayload(data, fallbackJobId = "") {
  const history =
    data?.history ||
    data?.caseAnalyzerHistory ||
    data?.case_analyzer_history ||
    data?.report?.history ||
    data?.report ||
    data ||
    {};

  const artifacts =
    data?.artifacts ||
    history?.artifacts ||
    data?.artifactPaths ||
    data?.artifact_paths ||
    history?.artifactPaths ||
    history?.artifact_paths ||
    {};

  const dashboardArtifact =
    data?.dashboardArtifact ||
    data?.dashboard_artifact ||
    history?.dashboardArtifact ||
    history?.dashboard_artifact ||
    {};

  return {
    ...data,
    history,
    report: data?.report || history,
    dashboard: data?.dashboard || history?.dashboard || dashboardArtifact?.dashboard || {},
    charts: data?.charts || history?.charts || dashboardArtifact?.charts || {},
    liveView:
      data?.liveView ||
      data?.live_view ||
      history?.liveView ||
      history?.live_view ||
      dashboardArtifact?.liveView ||
      dashboardArtifact?.live_view ||
      {},
    caseSnapshot: data?.caseSnapshot || data?.case_snapshot || history?.caseSnapshot || history?.case_snapshot || {},
    artifacts,
    artifactPaths: data?.artifactPaths || data?.artifact_paths || history?.artifactPaths || history?.artifact_paths || artifacts || {},
    dashboardArtifact,
    dashboard_artifact: dashboardArtifact,
    dashboardJsonPath:
      data?.dashboardJsonPath ||
      data?.dashboard_json_path ||
      artifacts?.dashboardJsonPath ||
      artifacts?.dashboard_json_path ||
      "",
    dashboard_json_path:
      data?.dashboard_json_path ||
      data?.dashboardJsonPath ||
      artifacts?.dashboard_json_path ||
      artifacts?.dashboardJsonPath ||
      "",
    dashboardLoadedFromArtifact: Boolean(data?.dashboardLoadedFromArtifact || data?.dashboard_loaded_from_artifact),
    dashboard_loaded_from_artifact: Boolean(data?.dashboard_loaded_from_artifact || data?.dashboardLoadedFromArtifact),
    dashboardArtifactError: data?.dashboardArtifactError || data?.dashboard_artifact_error || "",
    dashboard_artifact_error: data?.dashboard_artifact_error || data?.dashboardArtifactError || "",
    job_id: data?.job_id || data?.jobId || history?.jobId || history?.job_id || fallbackJobId,
    jobId: data?.jobId || data?.job_id || history?.jobId || history?.job_id || fallbackJobId,
  };
}

async function parseJsonResponse(res, fallbackLabel = "Case Analyzer API") {
  const contentType = res.headers?.get?.("content-type") || "";
  let data = null;
  let rawText = "";

  try {
    if (contentType.toLowerCase().includes("application/json")) {
      data = await res.json();
    } else {
      rawText = await res.text();
      if (rawText) {
        try {
          data = JSON.parse(rawText);
        } catch {
          data = null;
        }
      }
    }
  } catch {
    data = null;
  }

  if (!res.ok) {
    const reason = normalizeApiErrorMessage(data, res, fallbackLabel);
    const err = new Error(`${fallbackLabel} request failed: ${reason}`);
    err.status = res.status;
    err.statusCode = res.status;
    err.response = data;
    err.rawText = rawText;
    err.billing = data?.billing;
    err.job = data?.job;
    throw err;
  }

  if (data === null) {
    throw new Error(`${fallbackLabel} returned non-JSON response (status ${res.status}).`);
  }

  return data;
}

async function fetchJson(path, options = {}, fallbackLabel = "Case Analyzer API") {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    idToken,
    requireAuth = true,
    debug = false,
    debugCaseAnalyzer = false,
    ...fetchOptions
  } = options;

  const token = requireAuth ? requireIdToken({ idToken }, fallbackLabel) : String(idToken || "").trim();
  const { controller, timer } = withTimeout(timeoutMs);

  const headers = authHeaders(token, fetchOptions.headers || {});

  try {
    debugLog(
      { debug, debugCaseAnalyzer },
      "fetchJson",
      {
        method: fetchOptions.method || "GET",
        path,
        hasIdToken: Boolean(token),
      }
    );

    const res = await fetch(buildUrl(path), {
      credentials: "include",
      ...fetchOptions,
      signal: controller.signal,
      headers,
    });

    return await parseJsonResponse(res, fallbackLabel);
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error(`${fallbackLabel} timed out. Please try again.`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

function normalizeFileMeta(item, index, packageMeta = {}) {
  const file = item?.file;

  const clientId = item?.client_id || item?.clientId || `evidence_${index + 1}`;
  const evidenceType = item?.evidence_type || item?.evidenceType || item?.evidence_bundle_category || item?.evidenceBundleCategory || "";
  const evidenceNote = item?.evidence_note || item?.evidenceNote || "";
  const evidenceBundleId = item?.evidence_bundle_id || item?.evidenceBundleId || "";
  const templateId = item?.template_id || item?.templateId || item?.bundle_template_id || item?.bundleTemplateId || "";
  const evidenceBundleCategory = item?.evidence_bundle_category || item?.evidenceBundleCategory || evidenceType || "";
  const supportsSide = item?.supports_side || item?.supportsSide || "";
  const evidenceQuality = item?.evidence_quality || item?.evidenceQuality || "";
  const originalName = file?.name || item?.original_name || item?.originalName || item?.name || "";
  const contentType = file?.type || item?.content_type || item?.contentType || "application/octet-stream";
  const sizeBytes = file?.size || item?.size_bytes || item?.sizeBytes || item?.size || 0;
  const uploadOrder = item?.upload_order || item?.uploadOrder || index + 1;

  const detectedPageCount = toInt(
    item?.detected_page_count ??
      item?.detectedPageCount ??
      0,
    0
  );

  const manualPageCountRequired = Boolean(
    item?.manual_page_count_required ||
      item?.manualPageCountRequired ||
      String(item?.page_count_status || item?.pageCountStatus || "").toLowerCase() === "manual_required"
  );

  const manualPageCount = toInt(
    item?.manual_page_count ??
      item?.manualPageCount ??
      item?.manual_page_count_input ??
      item?.manualPageCountInput ??
      0,
    0
  );

  const pageCount = toInt(
    item?.page_count ??
      item?.pageCount ??
      item?.pages ??
      item?.page_total ??
      item?.pageTotal ??
      (manualPageCountRequired ? manualPageCount : detectedPageCount) ??
      0,
    0
  );

  const pageCountStatus = normalizePageCountStatus(
    manualPageCountRequired && pageCount > 0
      ? "user_provided"
      : item?.page_count_status || item?.pageCountStatus,
    pageCount
  );

  const pageCountSource =
    manualPageCountRequired && pageCount > 0
      ? "manual_user_entry"
      : item?.page_count_source ||
        item?.pageCountSource ||
        (pageCount > 0 ? "frontend" : "unknown");

  return {
    // snake_case fields used by the current backend
    client_id: clientId,
    evidence_type: evidenceType,
    evidence_note: evidenceNote,
    evidence_bundle_id: evidenceBundleId,
    template_id: templateId,
    evidence_bundle_category: evidenceBundleCategory,
    supports_side: supportsSide,
    importance: item?.importance || "",
    evidence_quality: evidenceQuality,
    date_range: item?.date_range || item?.dateRange || "",
    original_name: originalName,
    content_type: contentType,
    size_bytes: sizeBytes,
    upload_order: uploadOrder,

    // Page-count soft check metadata from frontend.
    // Backend storage helper enforces config limits when page_count is provided.
    // If the browser could not count the file, CaseAnalyzerPage.jsx requires
    // manual user entry before upload/analyze and sends it as page_count.
    page_count: pageCount,
    detected_page_count: detectedPageCount,
    manual_page_count: manualPageCount,
    manual_page_count_required: manualPageCountRequired,
    page_count_status: pageCountStatus,
    page_count_source: pageCountSource,

    // Whole-package page/billing metadata for upload-url soft checks.
    // The backend must still re-read billing and make the final package decision.
    billing_product: "case_analyzer",
    requested_page_count: packageMeta.requested_page_count || packageMeta.declared_total_page_count || pageCount,
    declared_total_page_count: packageMeta.declared_total_page_count || packageMeta.requested_page_count || pageCount,
    detected_total_page_count: packageMeta.detected_page_count || 0,
    user_entered_total_page_count: packageMeta.user_entered_page_count || 0,
    matched_page_tier: packageMeta.matched_page_tier || null,
    max_available_page_tier: packageMeta.max_available_page_tier || 0,
    upload_unknown_page_count_files: packageMeta.unknown_page_count_files || 0,
    upload_missing_manual_page_count_files: packageMeta.missing_manual_page_count_files || 0,

    // camelCase compatibility fields for future/alternate route handlers
    clientId,
    evidenceType,
    evidenceNote,
    evidenceBundleId,
    templateId,
    evidenceBundleCategory,
    supportsSide,
    evidenceQuality,
    dateRange: item?.date_range || item?.dateRange || "",
    originalName,
    contentType,
    sizeBytes,
    uploadOrder,
    pageCount,
    detectedPageCount,
    manualPageCount,
    manualPageCountRequired,
    pageCountStatus,
    pageCountSource,
    billingProduct: "case_analyzer",
    requestedPageCount: packageMeta.requestedPageCount || packageMeta.declaredTotalPageCount || pageCount,
    declaredTotalPageCount: packageMeta.declaredTotalPageCount || packageMeta.requestedPageCount || pageCount,
    detectedTotalPageCount: packageMeta.detectedPageCount || 0,
    userEnteredTotalPageCount: packageMeta.userEnteredPageCount || 0,
    matchedPageTier: packageMeta.matchedPageTier || null,
    maxAvailablePageTier: packageMeta.maxAvailablePageTier || 0,
    uploadUnknownPageCountFiles: packageMeta.unknownPageCountFiles || 0,
    uploadMissingManualPageCountFiles: packageMeta.missingManualPageCountFiles || 0,
  };
}

export async function createCaseAnalyzerJob(payload, opts = {}) {
  return fetchJson(
    "/case-analyzer/job",
    {
      method: "POST",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        payload,
        locale: opts.locale || getLocale(),
        client_nonce: opts.clientNonce || opts.client_nonce || makeClientNonce(),
        clientNonce: opts.clientNonce || opts.client_nonce || undefined,
      }),
    },
    "Create Case Analyzer job"
  );
}

export async function updateCaseAnalyzerJob(jobId, payload, opts = {}) {
  const id = requireJobId(jobId);
  return fetchJson(
    `/case-analyzer/job/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        payload,
        locale: opts.locale || getLocale(),
      }),
    },
    "Update Case Analyzer job"
  );
}

export async function listCaseAnalyzerJobs(opts = {}) {
  return fetchJson(
    "/case-analyzer/jobs",
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
    },
    "List Case Analyzer jobs"
  );
}

export async function deleteCaseAnalyzerJob(jobId, opts = {}) {
  const id = requireJobId(jobId);
  return fetchJson(
    `/case-analyzer/job/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
    },
    "Delete Case Analyzer job"
  );
}


export async function abandonCaseAnalyzerJob(jobId, details = {}, opts = {}) {
  const id = requireJobId(jobId);
  const reason = String(details?.reason || details?.message || "upload_session_failed").trim();
  const failureStage = String(
    details?.failure_stage ||
      details?.failureStage ||
      details?.upload_stage ||
      details?.uploadStage ||
      ""
  ).trim();

  const clientAbandonedAt = new Date().toISOString();

  return fetchJson(
    `/case-analyzer/job/${encodeURIComponent(id)}/abandon`,
    {
      method: "POST",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reason,
        failure_stage: failureStage,
        failureStage,
        file_name: details?.file_name || details?.fileName || "",
        fileName: details?.fileName || details?.file_name || "",
        file_number: Number(details?.file_number || details?.fileNumber || 0),
        fileNumber: Number(details?.fileNumber || details?.file_number || 0),
        total_files: Number(details?.total_files || details?.totalFiles || 0),
        totalFiles: Number(details?.totalFiles || details?.total_files || 0),
        elapsed_ms: Number(details?.elapsed_ms || details?.elapsedMs || 0),
        elapsedMs: Number(details?.elapsedMs || details?.elapsed_ms || 0),
        client_abandoned_at: clientAbandonedAt,
        clientAbandonedAt,
      }),
    },
    "Close failed Case Analyzer upload session"
  );
}

async function requestUploadUrl(jobId, fileMeta, opts = {}) {
  const id = requireJobId(jobId);

  debugLog(opts, "requestUploadUrl", {
    hasIdToken: Boolean(opts.idToken),
    jobId: id,
    fileName: fileMeta?.original_name || fileMeta?.originalName || fileMeta?.name,
    sizeBytes: fileMeta?.size_bytes || fileMeta?.sizeBytes || fileMeta?.size,
    contentType: fileMeta?.content_type || fileMeta?.contentType,
    pageCount: fileMeta?.page_count || fileMeta?.pageCount || 0,
    pageCountStatus: fileMeta?.page_count_status || fileMeta?.pageCountStatus || "unknown",
  });

  return fetchJson(
    `/case-analyzer/job/${encodeURIComponent(id)}/upload-url`,
    {
      method: "POST",
      timeoutMs: opts.uploadUrlTimeoutMs ?? DEFAULT_UPLOAD_URL_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fileMeta || {}),
    },
    "Create evidence upload URL"
  );
}

async function putFileToSignedUrl(uploadUrl, file, headers = {}, opts = {}) {
  if (!uploadUrl) throw new Error("Missing signed upload URL.");
  if (!file) throw new Error("Missing file for signed upload.");

  const timeoutMs = Math.max(
    1000,
    Number(opts.timeoutMs ?? opts.uploadTimeoutMs ?? DEFAULT_UPLOAD_TIMEOUT_MS)
  );
  const startedAt = nowMs();
  const startedAtIso = new Date().toISOString();
  const totalBytes = Number(file.size || 0);
  const fileName = file.name || "file";
  const progressContext = opts.progressContext || {};

  const emitProgress = ({ loadedBytes, lengthComputable = true }) => {
    const loaded = Math.max(0, Number(loadedBytes || 0));
    const total = totalBytes > 0 ? totalBytes : Math.max(0, Number(progressContext.totalBytes || 0));
    const elapsedMs = durationMs(startedAt);
    const percent = total > 0
      ? Math.max(0, Math.min(100, Math.round((loaded / total) * 100)))
      : 0;
    const averageBytesPerSecond = elapsedMs > 0
      ? Math.round((loaded * 1000) / elapsedMs)
      : 0;

    safeInvoke(opts.onProgress, {
      ...progressContext,
      type: "upload_progress",
      phase: "uploading_file",
      fileName,
      sizeBytes: total,
      loadedBytes: loaded,
      totalBytes: total,
      lengthComputable: Boolean(lengthComputable),
      percent,
      elapsedMs,
      averageBytesPerSecond,
      startedAt: startedAtIso,
    });
  };

  // Browser path: XMLHttpRequest provides reliable upload progress events.
  if (typeof XMLHttpRequest !== "undefined") {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let settled = false;
      let externalAbortHandler = null;

      const cleanup = () => {
        if (opts.signal && externalAbortHandler) {
          opts.signal.removeEventListener("abort", externalAbortHandler);
        }
      };

      const finishResolve = (value) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(value);
      };

      const finishReject = (error) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      };

      xhr.open("PUT", uploadUrl, true);
      xhr.timeout = timeoutMs;

      const uploadHeaders = sanitizeSignedUploadHeaders({
        "Content-Type": file.type || "application/octet-stream",
        ...(headers || {}),
      });

      Object.entries(uploadHeaders).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        xhr.setRequestHeader(key, String(value));
      });

      xhr.upload.onprogress = (event) => {
        emitProgress({
          loadedBytes: event.loaded,
          lengthComputable: event.lengthComputable,
        });
      };

      xhr.onload = () => {
        const elapsedMs = durationMs(startedAt);
        if (xhr.status >= 200 && xhr.status < 300) {
          emitProgress({ loadedBytes: totalBytes, lengthComputable: totalBytes > 0 });
          finishResolve({
            ok: true,
            status: xhr.status,
            elapsedMs,
            uploadedBytes: totalBytes,
            averageBytesPerSecond: elapsedMs > 0
              ? Math.round((totalBytes * 1000) / elapsedMs)
              : 0,
            startedAt: startedAtIso,
            completedAt: new Date().toISOString(),
          });
          return;
        }

        const detail = String(xhr.responseText || "").slice(0, 300);
        const error = new Error(
          `Signed upload failed for ${fileName} (HTTP ${xhr.status})${detail ? `: ${detail}` : ""}.`
        );
        error.status = xhr.status;
        error.statusCode = xhr.status;
        error.rawText = detail;
        finishReject(error);
      };

      xhr.onerror = () => {
        finishReject(new Error(`Signed upload network failure for ${fileName}.`));
      };

      xhr.ontimeout = () => {
        const error = new Error(`Signed upload timed out for ${fileName}.`);
        error.code = "UPLOAD_TIMEOUT";
        error.isTimeout = true;
        finishReject(error);
      };

      xhr.onabort = () => {
        const error = new Error(`Signed upload was stopped for ${fileName}.`);
        error.code = "UPLOAD_ABORTED";
        error.isAborted = true;
        finishReject(error);
      };

      if (opts.signal) {
        externalAbortHandler = () => xhr.abort();
        if (opts.signal.aborted) {
          xhr.abort();
          return;
        }
        opts.signal.addEventListener("abort", externalAbortHandler, { once: true });
      }

      // Important: never attach the Firebase Authorization header to this request.
      xhr.send(file);
    });
  }

  // Test/non-browser fallback. This keeps compatibility where XMLHttpRequest
  // is unavailable, but only the browser path can provide incremental progress.
  const { controller, timer } = withTimeout(timeoutMs);
  let externalAbortHandler = null;

  try {
    if (opts.signal) {
      externalAbortHandler = () => controller.abort();
      if (opts.signal.aborted) controller.abort();
      else opts.signal.addEventListener("abort", externalAbortHandler, { once: true });
    }

    const res = await fetch(uploadUrl, {
      method: "PUT",
      signal: controller.signal,
      headers: sanitizeSignedUploadHeaders({
        "Content-Type": file.type || "application/octet-stream",
        ...(headers || {}),
      }),
      body: file,
    });

    if (!res.ok) {
      let detail = "";
      try {
        detail = await res.text();
      } catch {
        detail = "";
      }
      const error = new Error(
        `Signed upload failed for ${fileName} (HTTP ${res.status})${detail ? `: ${detail.slice(0, 300)}` : ""}.`
      );
      error.status = res.status;
      error.statusCode = res.status;
      error.rawText = detail;
      throw error;
    }

    const elapsedMs = durationMs(startedAt);
    emitProgress({ loadedBytes: totalBytes, lengthComputable: totalBytes > 0 });
    return {
      ok: true,
      status: res.status,
      elapsedMs,
      uploadedBytes: totalBytes,
      averageBytesPerSecond: elapsedMs > 0
        ? Math.round((totalBytes * 1000) / elapsedMs)
        : 0,
      startedAt: startedAtIso,
      completedAt: new Date().toISOString(),
    };
  } catch (err) {
    if (err?.name === "AbortError") {
      const timeoutError = new Error(`Signed upload timed out for ${fileName}.`);
      timeoutError.code = opts.signal?.aborted ? "UPLOAD_ABORTED" : "UPLOAD_TIMEOUT";
      timeoutError.isAborted = Boolean(opts.signal?.aborted);
      timeoutError.isTimeout = !timeoutError.isAborted;
      throw timeoutError;
    }
    throw err;
  } finally {
    clearTimeout(timer);
    if (opts.signal && externalAbortHandler) {
      opts.signal.removeEventListener("abort", externalAbortHandler);
    }
  }
}

async function markUploadComplete(jobId, fileId, storagePath, fileMeta = {}, opts = {}) {
  const id = requireJobId(jobId);
  if (!fileId) throw new Error("Missing fileId.");
  if (!storagePath) throw new Error("Missing storagePath.");

  return fetchJson(
    `/case-analyzer/job/${encodeURIComponent(id)}/upload-complete`,
    {
      method: "POST",
      timeoutMs: opts.uploadCompleteTimeoutMs ?? DEFAULT_UPLOAD_COMPLETE_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        file_id: fileId,
        fileId,
        storage_path: storagePath,
        storagePath,

        // Evidence bundle routing metadata.
        // Keep this in upload-complete too, not only upload-url, so the backend
        // always has the selected 40-bundle template/category when it records
        // the completed file.
        evidence_type: fileMeta?.evidence_type || fileMeta?.evidenceType || "",
        evidenceType: fileMeta?.evidenceType || fileMeta?.evidence_type || "",
        evidence_note: fileMeta?.evidence_note || fileMeta?.evidenceNote || "",
        evidenceNote: fileMeta?.evidenceNote || fileMeta?.evidence_note || "",
        evidence_bundle_id: fileMeta?.evidence_bundle_id || fileMeta?.evidenceBundleId || "",
        evidenceBundleId: fileMeta?.evidenceBundleId || fileMeta?.evidence_bundle_id || "",
        template_id: fileMeta?.template_id || fileMeta?.templateId || "",
        templateId: fileMeta?.templateId || fileMeta?.template_id || "",
        evidence_bundle_category: fileMeta?.evidence_bundle_category || fileMeta?.evidenceBundleCategory || "",
        evidenceBundleCategory: fileMeta?.evidenceBundleCategory || fileMeta?.evidence_bundle_category || "",
        supports_side: fileMeta?.supports_side || fileMeta?.supportsSide || "",
        supportsSide: fileMeta?.supportsSide || fileMeta?.supports_side || "",
        importance: fileMeta?.importance || "",
        evidence_quality: fileMeta?.evidence_quality || fileMeta?.evidenceQuality || "",
        evidenceQuality: fileMeta?.evidenceQuality || fileMeta?.evidence_quality || "",
        date_range: fileMeta?.date_range || fileMeta?.dateRange || "",
        dateRange: fileMeta?.dateRange || fileMeta?.date_range || "",

        page_count: fileMeta?.page_count ?? fileMeta?.pageCount ?? 0,
        pageCount: fileMeta?.pageCount ?? fileMeta?.page_count ?? 0,
        detected_page_count: fileMeta?.detected_page_count ?? fileMeta?.detectedPageCount ?? 0,
        detectedPageCount: fileMeta?.detectedPageCount ?? fileMeta?.detected_page_count ?? 0,
        manual_page_count: fileMeta?.manual_page_count ?? fileMeta?.manualPageCount ?? 0,
        manualPageCount: fileMeta?.manualPageCount ?? fileMeta?.manual_page_count ?? 0,
        manual_page_count_required: Boolean(fileMeta?.manual_page_count_required || fileMeta?.manualPageCountRequired),
        manualPageCountRequired: Boolean(fileMeta?.manualPageCountRequired || fileMeta?.manual_page_count_required),
        page_count_status: fileMeta?.page_count_status || fileMeta?.pageCountStatus || "unknown",
        pageCountStatus: fileMeta?.pageCountStatus || fileMeta?.page_count_status || "unknown",
        page_count_source: fileMeta?.page_count_source || fileMeta?.pageCountSource || "unknown",
        pageCountSource: fileMeta?.pageCountSource || fileMeta?.page_count_source || "unknown",
        size_bytes: fileMeta?.size_bytes ?? fileMeta?.sizeBytes ?? 0,
        sizeBytes: fileMeta?.sizeBytes ?? fileMeta?.size_bytes ?? 0,
        original_name: fileMeta?.original_name || fileMeta?.originalName || "",
        originalName: fileMeta?.originalName || fileMeta?.original_name || "",
        content_type: fileMeta?.content_type || fileMeta?.contentType || "application/octet-stream",
        contentType: fileMeta?.contentType || fileMeta?.content_type || "application/octet-stream",
        client_id: fileMeta?.client_id || fileMeta?.clientId || "",
        clientId: fileMeta?.clientId || fileMeta?.client_id || "",
        billing_product: fileMeta?.billing_product || fileMeta?.billingProduct || "case_analyzer",
        billingProduct: fileMeta?.billingProduct || fileMeta?.billing_product || "case_analyzer",
        requested_page_count: fileMeta?.requested_page_count ?? fileMeta?.requestedPageCount ?? fileMeta?.declared_total_page_count ?? 0,
        requestedPageCount: fileMeta?.requestedPageCount ?? fileMeta?.requested_page_count ?? fileMeta?.declaredTotalPageCount ?? 0,
        declared_total_page_count: fileMeta?.declared_total_page_count ?? fileMeta?.declaredTotalPageCount ?? fileMeta?.requested_page_count ?? 0,
        declaredTotalPageCount: fileMeta?.declaredTotalPageCount ?? fileMeta?.declared_total_page_count ?? fileMeta?.requestedPageCount ?? 0,
        detected_total_page_count: fileMeta?.detected_total_page_count ?? fileMeta?.detectedTotalPageCount ?? 0,
        detectedTotalPageCount: fileMeta?.detectedTotalPageCount ?? fileMeta?.detected_total_page_count ?? 0,
        user_entered_total_page_count: fileMeta?.user_entered_total_page_count ?? fileMeta?.userEnteredTotalPageCount ?? 0,
        userEnteredTotalPageCount: fileMeta?.userEnteredTotalPageCount ?? fileMeta?.user_entered_total_page_count ?? 0,
        matched_page_tier: fileMeta?.matched_page_tier ?? fileMeta?.matchedPageTier ?? null,
        matchedPageTier: fileMeta?.matchedPageTier ?? fileMeta?.matched_page_tier ?? null,
        max_available_page_tier: fileMeta?.max_available_page_tier ?? fileMeta?.maxAvailablePageTier ?? 0,
        maxAvailablePageTier: fileMeta?.maxAvailablePageTier ?? fileMeta?.max_available_page_tier ?? 0,
      }),
    },
    "Confirm evidence upload"
  );
}

export async function uploadCaseAnalyzerEvidence(jobId, evidenceItems, opts = {}) {
  const id = requireJobId(jobId);
  requireIdToken(opts, "Upload Case Analyzer evidence");

  if (!Array.isArray(evidenceItems) || evidenceItems.length === 0) {
    throw new Error("Please attach at least one evidence item.");
  }

  const uploaded = [];
  const skipped = [];
  const packageMeta = buildEvidencePackageMeta(evidenceItems, opts);
  const totalFiles = evidenceItems.length;
  const batchStartedAt = nowMs();
  const batchStartedAtIso = new Date().toISOString();

  debugLog(opts, "uploadCaseAnalyzerEvidence.packageMeta", {
    jobId: id,
    requestedPageCount: packageMeta.requested_page_count,
    declaredTotalPageCount: packageMeta.declared_total_page_count,
    matchedPageTier: packageMeta.matched_page_tier,
    maxAvailablePageTier: packageMeta.max_available_page_tier,
    unknownPageCountFiles: packageMeta.unknown_page_count_files,
    missingManualPageCountFiles: packageMeta.missing_manual_page_count_files,
  });

  emitUploadEvent(opts, {
    type: "batch_start",
    phase: "uploading_evidence",
    jobId: id,
    totalFiles,
    uploadedCount: 0,
    batchStartedAt: batchStartedAtIso,
  });

  for (let index = 0; index < evidenceItems.length; index += 1) {
    const item = evidenceItems[index];
    const fileNumber = index + 1;
    const file = item?.file;
    const fileName = file?.name || item?.original_name || item?.originalName || `File ${fileNumber}`;
    const sizeBytes = Number(file?.size || item?.size_bytes || item?.sizeBytes || 0);
    const fileStartedAt = nowMs();
    const fileStartedAtIso = new Date().toISOString();
    const timings = {
      upload_url_ms: 0,
      file_upload_ms: 0,
      upload_complete_ms: 0,
      total_ms: 0,
    };

    const baseEvent = {
      jobId: id,
      fileIndex: fileNumber,
      fileNumber,
      totalFiles,
      fileName,
      sizeBytes,
      uploadedCount: uploaded.length,
      fileStartedAt: fileStartedAtIso,
      batchStartedAt: batchStartedAtIso,
    };

    emitUploadEvent(opts, {
      ...baseEvent,
      type: "file_start",
      phase: "requesting_upload_url",
      percent: 0,
      loadedBytes: 0,
      totalBytes: sizeBytes,
      elapsedMs: 0,
      timings: { ...timings },
    });

    try {
      if (!file) {
        throw createUploadStageError("missing_file", new Error("Evidence item is missing its browser File object."), {
          ...baseEvent,
          elapsedMs: durationMs(fileStartedAt),
          timings,
        });
      }

      const meta = normalizeFileMeta(item, index, packageMeta);

      emitUploadEvent(opts, {
        ...baseEvent,
        type: "phase_start",
        phase: "requesting_upload_url",
        elapsedMs: durationMs(fileStartedAt),
        timings: { ...timings },
      });

      const uploadUrlStartedAt = nowMs();
      let ticket;
      try {
        ticket = await requestUploadUrl(id, meta, opts);
        timings.upload_url_ms = durationMs(uploadUrlStartedAt);
      } catch (cause) {
        timings.upload_url_ms = durationMs(uploadUrlStartedAt);
        throw createUploadStageError("upload_url", cause, {
          ...baseEvent,
          elapsedMs: durationMs(fileStartedAt),
          timings: { ...timings },
        });
      }

      const uploadUrl = ticket?.upload_url || ticket?.uploadUrl || ticket?.url;
      const fileId = ticket?.file_id || ticket?.fileId || ticket?.file?.fileId || ticket?.file?.file_id;
      const storagePath = ticket?.storage_path || ticket?.storagePath || ticket?.file?.storagePath || ticket?.file?.storage_path;

      if (!uploadUrl || !fileId || !storagePath) {
        throw createUploadStageError(
          "upload_url_response",
          new Error("Upload URL response is missing upload_url, file_id, or storage_path."),
          {
            ...baseEvent,
            elapsedMs: durationMs(fileStartedAt),
            timings: { ...timings },
          }
        );
      }

      emitUploadEvent(opts, {
        ...baseEvent,
        type: "phase_start",
        phase: "uploading_file",
        fileId,
        storagePath,
        percent: 0,
        loadedBytes: 0,
        totalBytes: sizeBytes,
        elapsedMs: durationMs(fileStartedAt),
        timings: { ...timings },
      });

      const fileUploadStartedAt = nowMs();
      let uploadResult;
      try {
        // Important: never send the Firebase Authorization header to the signed URL.
        uploadResult = await putFileToSignedUrl(
          uploadUrl,
          file,
          ticket?.headers || {},
          {
            timeoutMs: opts.uploadTimeoutMs ?? DEFAULT_UPLOAD_TIMEOUT_MS,
            signal: opts.signal,
            progressContext: {
              ...baseEvent,
              fileId,
              storagePath,
            },
            onProgress: (progress) => {
              emitUploadEvent(opts, {
                ...progress,
                elapsedMs: durationMs(fileStartedAt),
                uploadElapsedMs: progress.elapsedMs,
                timings: {
                  ...timings,
                  file_upload_ms: progress.elapsedMs,
                },
              });
            },
          }
        );
        timings.file_upload_ms = durationMs(fileUploadStartedAt);
      } catch (cause) {
        timings.file_upload_ms = durationMs(fileUploadStartedAt);
        const stage = cause?.isTimeout || cause?.code === "UPLOAD_TIMEOUT"
          ? "file_upload_timeout"
          : "file_upload";
        throw createUploadStageError(stage, cause, {
          ...baseEvent,
          elapsedMs: durationMs(fileStartedAt),
          timings: { ...timings },
        });
      }

      emitUploadEvent(opts, {
        ...baseEvent,
        type: "phase_start",
        phase: "confirming_upload",
        fileId,
        storagePath,
        percent: 100,
        loadedBytes: sizeBytes,
        totalBytes: sizeBytes,
        elapsedMs: durationMs(fileStartedAt),
        uploadElapsedMs: timings.file_upload_ms,
        averageBytesPerSecond: uploadResult?.averageBytesPerSecond || 0,
        timings: { ...timings },
      });

      const confirmationStartedAt = nowMs();
      let confirmed;
      try {
        confirmed = await markUploadComplete(id, fileId, storagePath, meta, opts);
        timings.upload_complete_ms = durationMs(confirmationStartedAt);
      } catch (cause) {
        timings.upload_complete_ms = durationMs(confirmationStartedAt);
        throw createUploadStageError("upload_confirmation", cause, {
          ...baseEvent,
          elapsedMs: durationMs(fileStartedAt),
          timings: { ...timings },
        });
      }

      timings.total_ms = durationMs(fileStartedAt);

      const pageCount = meta.page_count || ticket?.page_count || ticket?.pageCount || 0;
      const detectedPageCount = meta.detected_page_count || ticket?.detected_page_count || ticket?.detectedPageCount || 0;
      const manualPageCount = meta.manual_page_count || ticket?.manual_page_count || ticket?.manualPageCount || 0;
      const manualPageCountRequired = Boolean(meta.manual_page_count_required || ticket?.manual_page_count_required || ticket?.manualPageCountRequired);
      const pageCountStatus = meta.page_count_status || ticket?.page_count_status || ticket?.pageCountStatus || "unknown";
      const pageCountSource = meta.page_count_source || ticket?.page_count_source || ticket?.pageCountSource || "unknown";

      const completedFile = {
        ...confirmed,
        file_id: fileId,
        fileId,
        storage_path: storagePath,
        storagePath,
        original_name: file.name,
        originalName: file.name,
        content_type: file.type || "application/octet-stream",
        contentType: file.type || "application/octet-stream",
        size_bytes: file.size || 0,
        sizeBytes: file.size || 0,
        page_count: pageCount,
        pageCount,
        detected_page_count: detectedPageCount,
        detectedPageCount,
        manual_page_count: manualPageCount,
        manualPageCount,
        manual_page_count_required: manualPageCountRequired,
        manualPageCountRequired,
        page_count_status: pageCountStatus,
        pageCountStatus,
        page_count_source: pageCountSource,
        pageCountSource,
        client_id: meta.client_id,
        clientId: meta.clientId,
        evidence_type: meta.evidence_type,
        evidenceType: meta.evidenceType,
        evidence_note: meta.evidence_note,
        evidenceNote: meta.evidenceNote,
        evidence_bundle_id: meta.evidence_bundle_id,
        evidenceBundleId: meta.evidenceBundleId,
        template_id: meta.template_id,
        templateId: meta.templateId,
        evidence_bundle_category: meta.evidence_bundle_category,
        evidenceBundleCategory: meta.evidenceBundleCategory,
        supports_side: meta.supports_side,
        supportsSide: meta.supportsSide,
        importance: meta.importance,
        evidence_quality: meta.evidence_quality,
        evidenceQuality: meta.evidenceQuality,
        date_range: meta.date_range,
        dateRange: meta.dateRange,
        requested_page_count: meta.requested_page_count,
        requestedPageCount: meta.requestedPageCount,
        declared_total_page_count: meta.declared_total_page_count,
        declaredTotalPageCount: meta.declaredTotalPageCount,
        matched_page_tier: meta.matched_page_tier,
        matchedPageTier: meta.matchedPageTier,
        max_available_page_tier: meta.max_available_page_tier,
        maxAvailablePageTier: meta.maxAvailablePageTier,
        upload_timings: { ...timings },
        uploadTimings: { ...timings },
        average_upload_bytes_per_second: uploadResult?.averageBytesPerSecond || 0,
        averageUploadBytesPerSecond: uploadResult?.averageBytesPerSecond || 0,
      };

      uploaded.push(completedFile);

      debugLog(opts, "uploadCaseAnalyzerEvidence.fileComplete", {
        jobId: id,
        fileNumber,
        totalFiles,
        fileName,
        sizeBytes,
        timings,
        averageBytesPerSecond: uploadResult?.averageBytesPerSecond || 0,
      });

      emitUploadEvent(opts, {
        ...baseEvent,
        type: "file_complete",
        phase: "completed",
        fileId,
        storagePath,
        percent: 100,
        loadedBytes: sizeBytes,
        totalBytes: sizeBytes,
        elapsedMs: timings.total_ms,
        uploadElapsedMs: timings.file_upload_ms,
        averageBytesPerSecond: uploadResult?.averageBytesPerSecond || 0,
        uploadedCount: uploaded.length,
        timings: { ...timings },
        file: completedFile,
      });
    } catch (cause) {
      const error = cause?.name === "CaseAnalyzerUploadError"
        ? cause
        : createUploadStageError("file_upload", cause, {
            ...baseEvent,
            elapsedMs: durationMs(fileStartedAt),
            timings: { ...timings, total_ms: durationMs(fileStartedAt) },
          });

      if (!error.timings?.total_ms) {
        error.timings = {
          ...(error.timings || timings),
          total_ms: durationMs(fileStartedAt),
        };
      }
      error.elapsedMs = Number(error.elapsedMs || error.timings.total_ms || durationMs(fileStartedAt));
      error.uploadedCount = uploaded.length;
      error.batchElapsedMs = durationMs(batchStartedAt);

      debugLog(opts, "uploadCaseAnalyzerEvidence.fileError", {
        jobId: id,
        fileNumber,
        totalFiles,
        fileName,
        sizeBytes,
        failureStage: error.failureStage,
        technicalMessage: error.technicalMessage,
        elapsedMs: error.elapsedMs,
        timings: error.timings,
      });

      emitUploadEvent(opts, {
        ...baseEvent,
        type: "file_error",
        phase: "failed",
        failureStage: error.failureStage,
        uploadStage: error.uploadStage,
        userMessage: error.userMessage,
        technicalMessage: error.technicalMessage,
        elapsedMs: error.elapsedMs,
        batchElapsedMs: error.batchElapsedMs,
        uploadedCount: uploaded.length,
        timings: error.timings,
        error,
      });

      // No automatic retry. The launcher decides when the user starts a new job.
      throw error;
    }
  }

  const batchElapsedMs = durationMs(batchStartedAt);
  const result = {
    ok: true,
    job_id: id,
    jobId: id,
    uploaded_count: uploaded.length,
    uploadedCount: uploaded.length,
    skipped_count: skipped.length,
    skippedCount: skipped.length,
    files: uploaded,
    skipped,
    batch_elapsed_ms: batchElapsedMs,
    batchElapsedMs,
    batch_started_at: batchStartedAtIso,
    batchStartedAt: batchStartedAtIso,
  };

  emitUploadEvent(opts, {
    type: "batch_complete",
    phase: "completed",
    jobId: id,
    totalFiles,
    uploadedCount: uploaded.length,
    skippedCount: skipped.length,
    elapsedMs: batchElapsedMs,
    batchElapsedMs,
    batchStartedAt: batchStartedAtIso,
    files: uploaded,
  });

  return result;
}

export async function deleteCaseAnalyzerEvidence(jobId, fileId, opts = {}) {
  const id = requireJobId(jobId);
  if (!fileId) throw new Error("Missing fileId.");

  return fetchJson(
    `/case-analyzer/job/${encodeURIComponent(id)}/evidence/${encodeURIComponent(fileId)}`,
    {
      method: "DELETE",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
    },
    "Delete evidence file"
  );
}

export async function startCaseAnalyzerRun(jobId, payload, opts = {}) {
  const id = requireJobId(jobId);

  debugLog(opts, "startCaseAnalyzerRun", {
    hasIdToken: Boolean(opts.idToken),
    jobId: id,
    totalPageCount: payload?.evidence_package?.total_page_count || payload?.evidencePackage?.totalPageCount || 0,
    declaredTotalPageCount: payload?.evidence_package?.declared_total_page_count || payload?.billing_package?.declared_total_page_count || 0,
    requestedPageCount: payload?.billing_package?.requested_page_count || payload?.evidence_package?.declared_total_page_count || 0,
    detectedPageCount: payload?.evidence_package?.detected_page_count || 0,
    userEnteredPageCount: payload?.evidence_package?.user_entered_page_count || 0,
    matchedPageTier: payload?.billing_package?.matched_page_tier || payload?.evidence_package?.matched_case_analyzer_page_tier || null,
    maxAvailablePageTier: payload?.billing_package?.max_available_page_tier || payload?.evidence_package?.max_available_case_analyzer_page_tier || 0,
    unknownPageCountFiles: payload?.evidence_package?.unknown_page_count_files || payload?.evidencePackage?.unknownPageCountFiles || 0,
    missingManualPageCountFiles: payload?.evidence_package?.missing_manual_page_count_files || payload?.billing_package?.missing_manual_page_count_files || 0,
  });

  try {
    const data = await fetchJson(
      `/case-analyzer/job/${encodeURIComponent(id)}/analyze`,
      {
        method: "POST",
        timeoutMs: opts.timeoutMs ?? DEFAULT_START_TIMEOUT_MS,
        idToken: opts.idToken,
        debug: opts.debug,
        debugCaseAnalyzer: opts.debugCaseAnalyzer,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payload,
          locale: opts.locale || getLocale(),
          client_nonce: opts.clientNonce || opts.client_nonce || makeClientNonce(),
          clientNonce: opts.clientNonce || opts.client_nonce || undefined,
        }),
      },
      "Start Case Analyzer job"
    );

    return normalizeJobStatusPayload(data, id);
  } catch (err) {
    // Important frontend handoff rule:
    // If the backend says the job is already locked/queued/processing, the
    // launcher should still move to the status page. The worker may already be
    // processing or may even complete successfully.
    if (isCaseAnalyzerAlreadyStartedError(err)) {
      return normalizeJobStatusPayload(
        {
          ok: true,
          alreadyStarted: true,
          alreadyLocked: true,
          job_id: id,
          jobId: id,
          status: err?.response?.status || err?.job?.status || "queued",
          message:
            err?.response?.message ||
            err?.response?.error ||
            "This Case Analyzer job is already locked or processing.",
          job: err?.job || err?.response?.job || { job_id: id, jobId: id, status: "queued" },
          response: err?.response || null,
        },
        id
      );
    }

    throw err;
  }
}

export async function getCaseAnalyzerStatus(jobId, opts = {}) {
  const id = requireJobId(jobId);
  const data = await fetchJson(
    `/case-analyzer/status/${encodeURIComponent(id)}`,
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
    },
    "Get Case Analyzer status"
  );

  return normalizeJobStatusPayload(data, id);
}

export async function getCaseAnalyzerReport(jobId, opts = {}) {
  const id = requireJobId(jobId);
  const data = await fetchJson(
    `/case-analyzer/report/${encodeURIComponent(id)}`,
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
    },
    "Get Case Analyzer report"
  );

  return normalizeHistoryPayload(data, id);
}


export async function listCaseAnalyzerHistory(opts = {}) {
  const requestedPage = Number.parseInt(opts.page ?? 1, 10);
  const requestedPageSize = Number.parseInt(
    opts.pageSize ??
      opts.page_size ??
      opts.limit ??
      opts.maxResults ??
      8,
    10,
  );

  const page = Number.isFinite(requestedPage)
    ? Math.max(1, requestedPage)
    : 1;
  const pageSize = Number.isFinite(requestedPageSize)
    ? Math.max(1, requestedPageSize)
    : 8;

  const query = String(opts.query ?? opts.search ?? "").trim();
  const status = String(
    opts.status ??
      opts.statusFilter ??
      opts.status_filter ??
      "all",
  ).trim() || "all";
  const sort = String(
    opts.sort ??
      opts.sortKey ??
      opts.sort_key ??
      "newest",
  ).trim() || "newest";
  const showAll = Boolean(opts.showAll ?? opts.show_all ?? false);
  const includeDeleted = Boolean(
    opts.includeDeleted ?? opts.include_deleted ?? false,
  );

  // Updated history-page default:
  // - page_size is the number of visible rows (normally 8)
  // - lookahead=1 makes the backend inspect one extra row (normally 9 total)
  // - include_stats=0 keeps Next/Previous requests lightweight
  const lookahead = Boolean(
    opts.lookahead ??
      opts.lookAhead ??
      opts.fetchNext ??
      opts.fetch_next ??
      true,
  );
  const includeStats = Boolean(
    opts.includeStats ??
      opts.include_stats ??
      false,
  );

  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("page_size", String(pageSize));

  // Compatibility alias for older deployments. This is the current visible
  // page size only; it is not an overall history-record limit.
  qs.set("limit", String(pageSize));

  qs.set("lookahead", lookahead ? "1" : "0");
  qs.set("include_stats", includeStats ? "1" : "0");

  if (query) qs.set("query", query);
  qs.set("status", status);
  qs.set("sort", sort);
  qs.set("show_all", showAll ? "true" : "false");
  qs.set("include_deleted", includeDeleted ? "true" : "false");

  return fetchJson(
    `/case-analyzer/history?${qs.toString()}`,
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
    },
    "List Case Analyzer history"
  );
}

export async function getCaseAnalyzerHistoryStats(opts = {}) {
  const showAll = Boolean(opts.showAll ?? opts.show_all ?? false);
  const includeDeleted = Boolean(
    opts.includeDeleted ?? opts.include_deleted ?? false,
  );

  const qs = new URLSearchParams();
  qs.set("show_all", showAll ? "true" : "false");
  qs.set("include_deleted", includeDeleted ? "true" : "false");

  return fetchJson(
    `/case-analyzer/history/stats?${qs.toString()}`,
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
    },
    "Get Case Analyzer history stats"
  );
}

export async function renameCaseAnalyzerHistory(jobId, title, opts = {}) {
  const id = requireJobId(jobId);
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) throw new Error("Please enter a name for this analysis.");

  return fetchJson(
    `/case-analyzer/history/${encodeURIComponent(id)}/rename`,
    {
      method: "PATCH",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: cleanTitle,
        displayTitle: cleanTitle,
      }),
    },
    "Rename Case Analyzer history item"
  );
}

export async function archiveCaseAnalyzerHistory(jobId, opts = {}) {
  const id = requireJobId(jobId);

  return fetchJson(
    `/case-analyzer/history/${encodeURIComponent(id)}/archive`,
    {
      method: "PATCH",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ archived: true }),
    },
    "Archive Case Analyzer history item"
  );
}

export async function restoreCaseAnalyzerHistory(jobId, opts = {}) {
  const id = requireJobId(jobId);

  return fetchJson(
    `/case-analyzer/history/${encodeURIComponent(id)}/restore`,
    {
      method: "PATCH",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ archived: false }),
    },
    "Restore Case Analyzer history item"
  );
}

export async function deleteCaseAnalyzerHistory(jobId, opts = {}) {
  const id = requireJobId(jobId);

  return fetchJson(
    `/case-analyzer/history/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      debug: opts.debug,
      debugCaseAnalyzer: opts.debugCaseAnalyzer,
    },
    "Delete Case Analyzer history item"
  );
}


export async function getCaseAnalyzerHistory(jobId, opts = {}) {
  const id = requireJobId(jobId);

  // Preferred future route. If the backend does not have this route yet,
  // fall back to /case-analyzer/report/{jobId}, which can return the same
  // user-facing history/dashboard document.
  try {
    const data = await fetchJson(
      `/case-analyzer/history/${encodeURIComponent(id)}`,
      {
        method: "GET",
        timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
        idToken: opts.idToken,
        debug: opts.debug,
        debugCaseAnalyzer: opts.debugCaseAnalyzer,
      },
      "Get Case Analyzer history"
    );

    return normalizeHistoryPayload(data, id);
  } catch (err) {
    if (Number(err?.status || err?.statusCode || 0) !== 404) throw err;
    return getCaseAnalyzerReport(id, opts);
  }
}


export async function getCaseAnalyzerDashboard(jobId, opts = {}) {
  const id = requireJobId(jobId);

  try {
    const data = await fetchJson(
      `/case-analyzer/dashboard/${encodeURIComponent(id)}`,
      {
        method: "GET",
        timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
        idToken: opts.idToken,
        debug: opts.debug,
        debugCaseAnalyzer: opts.debugCaseAnalyzer,
      },
      "Get Case Analyzer dashboard"
    );

    return normalizeHistoryPayload(data, id);
  } catch (err) {
    // Compatibility fallback while routes are rolling out.
    if (Number(err?.status || err?.statusCode || 0) !== 404) throw err;
    return getCaseAnalyzerHistory(id, opts);
  }
}

const caseAnalyzerApiClient = {
  createCaseAnalyzerJob,
  updateCaseAnalyzerJob,
  listCaseAnalyzerJobs,
  deleteCaseAnalyzerJob,
  abandonCaseAnalyzerJob,
  uploadCaseAnalyzerEvidence,
  deleteCaseAnalyzerEvidence,
  startCaseAnalyzerRun,
  getCaseAnalyzerStatus,
  getCaseAnalyzerReport,
  getCaseAnalyzerHistory,
  listCaseAnalyzerHistory,
  getCaseAnalyzerHistoryStats,
  renameCaseAnalyzerHistory,
  archiveCaseAnalyzerHistory,
  restoreCaseAnalyzerHistory,
  deleteCaseAnalyzerHistory,
  getCaseAnalyzerDashboard,
  isCaseAnalyzerAlreadyStartedError,
  getCaseAnalyzerUploadUserMessage,
};

export default caseAnalyzerApiClient;
